import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Avatar,
  ButtonDS,
  NavigationArrows,
  RemoveRedEyeOutlined,
  SidePanelDS,
  Spacer,
  Tag,
  dateFormatterWeekDayDayMonthYear,
} from "@qivia/ui";
import { colors } from "@qivia/ui/src/styles/figmaColors";
import { typographies } from "@qivia/ui/src/styles/figmaTypographies";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../../../redux/hooks";
import { selectCompany } from "../../../homeSlice";
import type { QueryStatus } from "../../../../../utils";
import type { VehicleSession, VehicleSessionRestitution } from "../../fleetApi";
import {
  restitutionCorrectivePicturesAsync,
  restitutionInitialPicturesAsync,
  selectRestitutionCorrectivePicturesStatus,
  selectRestitutionInitialPicturesStatus,
  selectRestitutionPictures,
} from "../../fleetSlice";
import { RestitutionDetails } from "./RestitutionDetails";
import { RestitutionVehicleState } from "./RestitutionVehicleState";
import { UpdateRestitutionModal } from "./UpdateRestitutionModal";

interface VehicleSessionSidePanelProps {
  sessionWithFinalRestitution: VehicleSession;
  previousSessionId: string | null;
  nextSessionId: string | null;
}

export const VehicleSessionSidePanel = (
  props: VehicleSessionSidePanelProps,
) => {
  const { sessionWithFinalRestitution, previousSessionId, nextSessionId } =
    props;
  const navigate = useNavigate();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const company = useAppSelector(selectCompany);
  const [isUpdateRestitutionModalVisible, setIsUpdateRestitutionModalVisible] =
    useState(false);
  const restitutionInitialPicturesStatus = useAppSelector(
    selectRestitutionInitialPicturesStatus,
  );
  const restitutionCorrectivePicturesStatus = useAppSelector(
    selectRestitutionCorrectivePicturesStatus,
  );
  const restitutionPictures = useAppSelector(selectRestitutionPictures);
  const firstPath = location.pathname.split("/")[1];

  const { initialRestitution, correctiveRestitution } = useMemo(() => {
    const withPictures = (
      restitution: VehicleSessionRestitution | undefined,
      picturesStatus: QueryStatus,
    ) =>
      restitution
        ? {
            ...restitution,
            picturesUrls: restitutionPictures[restitution.uuid] ?? null,
            picturesStatus,
          }
        : undefined;

    return {
      initialRestitution: withPictures(
        sessionWithFinalRestitution.restitutions.find(
          (r) => r.declarationType === "INITIAL",
        ),
        restitutionInitialPicturesStatus,
      ),
      correctiveRestitution: withPictures(
        sessionWithFinalRestitution.restitutions.find(
          (r) => r.declarationType === "CORRECTIVE",
        ),
        restitutionCorrectivePicturesStatus,
      ),
    };
  }, [
    sessionWithFinalRestitution.restitutions,
    restitutionPictures,
    restitutionInitialPicturesStatus,
    restitutionCorrectivePicturesStatus,
  ]);

  const finalRestitution = correctiveRestitution ?? initialRestitution;

  const isModalVisible =
    finalRestitution !== undefined && isUpdateRestitutionModalVisible;

  const collapsedInitialRestitution = useMemo(
    () => (correctiveRestitution ? initialRestitution : undefined),
    [correctiveRestitution, initialRestitution],
  );

  useEffect(() => {
    if (
      company &&
      initialRestitution &&
      !initialRestitution.picturesUrls &&
      initialRestitution.picturesStatus === "idle"
    ) {
      void dispatch(restitutionInitialPicturesAsync(initialRestitution.uuid));
    }
  }, [company, dispatch, initialRestitution]);

  useEffect(() => {
    if (
      company &&
      correctiveRestitution &&
      !correctiveRestitution.picturesUrls &&
      correctiveRestitution.picturesStatus === "idle"
    ) {
      void dispatch(
        restitutionCorrectivePicturesAsync(correctiveRestitution.uuid),
      );
    }
  }, [company, dispatch, correctiveRestitution]);

  const formattedDate = useMemo(
    () =>
      dateFormatterWeekDayDayMonthYear(
        new Date(sessionWithFinalRestitution.startOfUsage),
      ),
    [sessionWithFinalRestitution.startOfUsage],
  );

  const initials = useMemo(
    () =>
      sessionWithFinalRestitution.driverName
        .split(" ")
        .map((word) => word.charAt(0)),
    [sessionWithFinalRestitution.driverName],
  );

  const onSidePanelClosed = useCallback(() => {
    navigate(`/${firstPath}/fleet/vehicle_sessions`);
  }, [navigate, firstPath]);

  const handleClickVehicle = useCallback(() => {
    navigate(
      `/${firstPath}/vehicles/${sessionWithFinalRestitution.vehicleUuid}?fromNavigate=true`,
    );
  }, [navigate, firstPath, sessionWithFinalRestitution.vehicleUuid]);

  const handleClickDriver = useCallback(() => {
    navigate(
      `/${firstPath}/drivers/${sessionWithFinalRestitution.driverUuid}?fromNavigate=true`,
    );
  }, [navigate, firstPath, sessionWithFinalRestitution.driverUuid]);

  const onPreviousSession = useCallback(() => {
    if (!previousSessionId) {
      return;
    }
    navigate(`/${firstPath}/fleet/vehicle_sessions/${previousSessionId}`);
  }, [navigate, firstPath, previousSessionId]);

  const onNextSession = useCallback(() => {
    if (!nextSessionId) {
      return;
    }
    navigate(`/${firstPath}/fleet/vehicle_sessions/${nextSessionId}`);
  }, [navigate, firstPath, nextSessionId]);

  return (
    <SidePanelDS.SidePanel
      visible={!!sessionWithFinalRestitution}
      onClose={onSidePanelClosed}
      withoutOverflow={true}
      absoluteSection={
        finalRestitution ? (
          <ButtonDS
            text={t(
              "fleet.vehicleSessions.sidepanel.updateRestitutionModal.open",
            )}
            onClick={() => setIsUpdateRestitutionModalVisible(true)}
            buttonType={"primary"}
            format={"fill"}
          />
        ) : undefined
      }
    >
      <StyledSidePanelContainer>
        <SidePanelDS.Section
          verticalSpacing={1.2}
          horizontalSpacing={1.5}
          background={colors["colors/button/secondary/hover"]}
        >
          <StyledPanelNavigation>
            <StyledNavigationArrows>
              <NavigationArrows
                title={formattedDate}
                onPrevious={onPreviousSession}
                onNext={onNextSession}
              />
            </StyledNavigationArrows>
            <Spacer y={1.5} />
            <SidePanelDS.ActionBlock>
              <SidePanelDS.SectionedActionStripe
                left={
                  <StyledPerson>
                    <Avatar
                      size={"S"}
                      color={"mint"}
                      firstName={initials[0]}
                      lastName={initials[initials.length - 1]}
                    />
                    <Spacer x={0.5} />
                    {sessionWithFinalRestitution.driverName}
                  </StyledPerson>
                }
                right={
                  <StyledLittleIcon>
                    <RemoveRedEyeOutlined onClick={handleClickDriver} />
                    <Spacer x={1} />
                  </StyledLittleIcon>
                }
              />
            </SidePanelDS.ActionBlock>
            <Spacer y={1} />
            <SidePanelDS.ActionBlock>
              <SidePanelDS.SectionedActionStripe
                left={
                  <StyledVehicle>
                    {sessionWithFinalRestitution.registrationNumber ?? "-"}
                    {sessionWithFinalRestitution.reference && (
                      <>
                        <Spacer x={0.5} />
                        <Tag
                          text={sessionWithFinalRestitution.reference}
                          borderColor={colors["colors/borders/cells/cells"]}
                          backgroundColor={
                            colors[
                              "colors/surfaces/background/background_level0"
                            ]
                          }
                          textColor={colors["colors/button/primary/default"]}
                        />
                      </>
                    )}
                  </StyledVehicle>
                }
                right={
                  <StyledLittleIcon>
                    <RemoveRedEyeOutlined onClick={handleClickVehicle} />
                    <Spacer x={1} />
                  </StyledLittleIcon>
                }
              />
            </SidePanelDS.ActionBlock>
          </StyledPanelNavigation>
        </SidePanelDS.Section>

        <StyledScrollableContent>
          <RestitutionDetails
            session={sessionWithFinalRestitution}
            initialRestitution={initialRestitution}
            correctiveRestitution={correctiveRestitution}
          />

          <StyledSeparator />

          <RestitutionVehicleState
            finalRestitution={finalRestitution}
            collapsedInitialRestitution={collapsedInitialRestitution}
          />
          {finalRestitution && <Spacer y={6} />}
        </StyledScrollableContent>

        {isModalVisible && (
          <UpdateRestitutionModal
            visible={isModalVisible}
            onClose={() => setIsUpdateRestitutionModalVisible(false)}
            driversHistory={sessionWithFinalRestitution.uuid}
            driverName={sessionWithFinalRestitution.driverName}
            registrationNumber={sessionWithFinalRestitution.registrationNumber}
            departureLocation={initialRestitution?.departureLocation ?? null}
            furthestDestination={
              initialRestitution?.furthestDestination ?? null
            }
            mileage={finalRestitution.mileage}
            isCabinCleaned={finalRestitution.isCabinCleaned}
            isTrunkCleaned={finalRestitution.isTrunkCleaned}
            observations={finalRestitution.observations}
            pictures={finalRestitution.picturesUrls ?? []}
          />
        )}
      </StyledSidePanelContainer>
    </SidePanelDS.SidePanel>
  );
};

const StyledVehicle = styled.div`
  ${typographies["Body/S"]}
  display: flex;
  align-items: center;
`;

const StyledPerson = styled.div`
  ${typographies["Body/S"]}
  display: flex;
  align-items: center;
`;

const StyledLittleIcon = styled.div`
  display: flex;
  cursor: pointer;
  & svg {
    width: 1rem;
    height: 1rem;
  }
`;

const StyledSeparator = styled.div`
  width: 100%;
  border-top: 0.0625rem solid ${colors["colors/borders/cards/cards"]};
`;

const StyledSidePanelContainer = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const StyledScrollableContent = styled.div`
  min-height: 0;
  overflow-y: auto;
`;

const StyledPanelNavigation = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const StyledNavigationArrows = styled.div`
  width: 80%;
  z-index: 2;
`;
